import React, { useCallback, useRef, useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { Tooltip } from "@/shared";
import { Icons } from "@/shared";
import {
  formatIsoDateToDDMMYYYY,
  formatToDDMMYYYY,
} from "@/shared/utils/date";
import {
  InputContainer,
  Label,
  DateInput,
  DateSeparator,
  DateInputContainer,
  InputWrapper,
  HelpOverlay,
  HelpArea,
} from "../Input.styles";
import {
  DateFieldProps,
  DateFieldState,
  DateFieldHandlers,
} from "./DateField.types";
import {
  parseDateString,
  formatDayValue,
  formatMonthValue,
  normalizeNumericInput,
  shouldAutoAdvance,
  isValidDay,
  isValidMonth,
  getCurrentYear,
  DateValidationOptions,
} from "./date-utils";

const DDMMYYYY_DATE_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}/;

const normalizeDateForDisplay = (value: string): string => {
  const trimmedValue = value.trim();

  if (DDMMYYYY_DATE_REGEX.test(trimmedValue)) {
    return trimmedValue;
  }

  if (ISO_DATE_REGEX.test(trimmedValue)) {
    return formatIsoDateToDDMMYYYY(trimmedValue);
  }

  return formatToDDMMYYYY(trimmedValue);
};

export const DateField: React.FC<DateFieldProps> = ({
  id,
  name,
  label,
  disabled = false,
  className,
  helpText,
  helpTooltipPosition = "inputInfo",
  dateGranularity,
  minYear = 1900,
  maxYear = getCurrentYear() + 10,
  maxDate,
  required = false,
  autoComplete,
  onDateChange,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formik = useFormikContext<Record<string, any>>();

  // Estado interno para los campos de fecha
  const [dateState, setDateState] = useState<DateFieldState>({
    day: "",
    month: "",
    year: "",
  });

  // Referencias a los inputs (Estabilizadas)
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Referencia para evitar loops de sincronización
  const lastFormikValue = useRef<string>("");

  // Opciones de validación
  const validationOptions: DateValidationOptions = useMemo(
    () => ({
      minYear,
      maxYear,
      maxDate,
    }),
    [maxDate, maxYear, minYear],
  );

  /**
   * Helper unificado para actualizar el estado local y Formik simultáneamente.
   * Evita efectos secundarios y pings innecesarios.
   */
  const updateStateAndFormik = useCallback(
    (newState: DateFieldState) => {
      // 1. Actualizar estado local
      setDateState(newState);

      // 2. Calcular valor formateado inmediatamente usando el nuevo estado
      let formattedValue = "";

      if (dateGranularity === "day") {
        if (newState.day && newState.month && newState.year) {
          const day = parseInt(newState.day, 10);
          const month = parseInt(newState.month, 10);
          const year = parseInt(newState.year, 10);

          if (isValidDay(day, month, year, validationOptions)) {
            formattedValue = formatDayValue(day, month, year);
          }
        }
      } else {
        if (newState.month && newState.year) {
          const month = parseInt(newState.month, 10);
          const year = parseInt(newState.year, 10);

          if (isValidMonth(month, year, validationOptions)) {
            formattedValue = formatMonthValue(month, year);
          }
        }
      }

      // 3. Sincronizar con Formik solo si cambió
      const currentFormikValue = formik.values[name] || "";
      if (currentFormikValue !== formattedValue) {
        lastFormikValue.current = formattedValue;
        formik.setFieldValue(name, formattedValue);
        onDateChange?.(formattedValue);
      }
    },
    [dateGranularity, formik, name, onDateChange, validationOptions]
  );

  // Inicializar valores desde Formik (Inbound Sync)
  // Se mantiene solo para cuando el valor viene desde afuera (ej: carga inicial o reset)
  useEffect(() => {
    const currentValue = formik.values[name] || "";
    const normalizedValue = currentValue
      ? normalizeDateForDisplay(String(currentValue))
      : "";

    if (normalizedValue !== lastFormikValue.current) {
      lastFormikValue.current = normalizedValue;

      if (normalizedValue) {
        const parsed = parseDateString(normalizedValue);
        setDateState({
          day: parsed.day ? parsed.day.toString() : "",
          month: parsed.month ? parsed.month.toString() : "",
          year: parsed.year ? parsed.year.toString() : "",
        });
      } else {
        setDateState({
          day: "",
          month: "",
          year: "",
        });
      }
    }
  }, [formik.values, name]);

  // Manejadores de eventos
  const handlers: DateFieldHandlers = {
    handleDayChange: useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = normalizeNumericInput(e.target.value, 2);
        const numValue = parseInt(newValue, 10);

        // Validar input: permitir ceros iniciales mientras sea longitud <= 2
        // Si el valor numérico es válido (0-31) y longitud <= 2, aceptamos.
        if (
          newValue === "" ||
          (newValue.length <= 2 && numValue >= 0 && numValue <= 31)
        ) {
          // Crear nuevo estado explícito
          const newState = { ...dateState, day: newValue };

          // Actualizar todo de una vez
          updateStateAndFormik(newState);

          // Auto-avance al mes: solo si tiene 2 dígitos exactos
          if (newValue.length === 2 && shouldAutoAdvance(newValue, 2, 31)) {
            monthRef.current?.focus();
          }
        }
      },
      [dateState, updateStateAndFormik]
    ),

    handleMonthChange: useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = normalizeNumericInput(e.target.value, 2);
        const numValue = parseInt(newValue, 10);

        // Validar rango de mes: permitir ceros iniciales
        if (
          newValue === "" ||
          (newValue.length <= 2 && numValue >= 0 && numValue <= 12)
        ) {
          const newState = { ...dateState, month: newValue };
          updateStateAndFormik(newState);

          // Auto-avance al año: solo si tiene 2 dígitos exactos
          if (newValue.length === 2 && shouldAutoAdvance(newValue, 2, 12)) {
            yearRef.current?.focus();
          }
        }
      },
      [dateState, updateStateAndFormik]
    ),

    handleYearChange: useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = normalizeNumericInput(e.target.value, 4);
        const newState = { ...dateState, year: newValue };
        updateStateAndFormik(newState);
      },
      [dateState, updateStateAndFormik]
    ),

    handleFocus: useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    }, []),

    handlePaste: useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text");
        const parsed = parseDateString(pastedText);

        if (parsed.day || parsed.month || parsed.year) {
          const newState = {
            day:
              dateGranularity === "day" && parsed.day
                ? parsed.day.toString()
                : dateState.day,
            month: parsed.month ? parsed.month.toString() : dateState.month,
            year: parsed.year ? parsed.year.toString() : dateState.year,
          };

          updateStateAndFormik(newState);

          // Enfocar el siguiente campo apropiado
          if (
            dateGranularity === "day" &&
            parsed.day &&
            parsed.month &&
            !parsed.year
          ) {
            yearRef.current?.focus();
          } else if (parsed.month && !parsed.year) {
            yearRef.current?.focus();
          }
        }
      },
      [dateGranularity, dateState, updateStateAndFormik]
    ),

    handleKeyDown: useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        const currentField = target.placeholder;

        // Navegación con flechas
        if (
          e.key === "ArrowRight" &&
          target.selectionStart === target.value.length
        ) {
          if (currentField === "dd" && dateGranularity === "day") {
            e.preventDefault();
            monthRef.current?.focus();
          } else if (currentField === "mm") {
            e.preventDefault();
            yearRef.current?.focus();
          }
        } else if (e.key === "ArrowLeft" && target.selectionStart === 0) {
          if (currentField === "aaaa") {
            e.preventDefault();
            monthRef.current?.focus();
          } else if (currentField === "mm" && dateGranularity === "day") {
            e.preventDefault();
            dayRef.current?.focus();
          }
        }

        // Backspace para navegar hacia atrás cuando el campo está vacío
        if (e.key === "Backspace" && target.value === "") {
          if (currentField === "aaaa") {
            e.preventDefault();
            monthRef.current?.focus();
          } else if (currentField === "mm" && dateGranularity === "day") {
            e.preventDefault();
            dayRef.current?.focus();
          }
        }
      },
      [dateGranularity]
    ),

    handleBlur: useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const target = e.target;
        const value = target.value;
        const placeholder = target.placeholder;

        if (value.length === 1 && /^\d$/.test(value)) {
          const padded = "0" + value;
          const newState = { ...dateState };

          if (placeholder === "dd") {
            newState.day = padded;
          } else if (placeholder === "mm") {
            newState.month = padded;
          }

          // Actualizar si hubo cambios de padding
          if (
            newState.day !== dateState.day ||
            newState.month !== dateState.month
          ) {
            updateStateAndFormik(newState);
          }
        }
      },
      [dateState, updateStateAndFormik]
    ),
  };

  // Renderizar inputs según granularidad
  const renderInputs = () => {
    const inputs = [];

    // Input de día (solo para granularidad 'day')
    if (dateGranularity === "day") {
      inputs.push(
        <DateInput
          key="day"
          id={`${id}-day`}
          name={`${name}Day`}
          ref={dayRef}
          type="text"
          placeholder="dd"
          value={dateState.day}
          onChange={handlers.handleDayChange}
          onFocus={handlers.handleFocus}
          onBlur={handlers.handleBlur}
          onPaste={handlers.handlePaste}
          onKeyDown={handlers.handleKeyDown}
          disabled={disabled}
          maxLength={2}
          autoComplete={autoComplete === "bday" ? "bday-day" : autoComplete}
          style={{ width: "40px" }}
          aria-label="Día"
        />,
        <DateSeparator key="sep1">/</DateSeparator>
      );
    }

    // Input de mes
    inputs.push(
      <DateInput
        key="month"
        id={`${id}-month`}
        name={`${name}Month`}
        ref={monthRef}
        type="text"
        placeholder="mm"
        value={dateState.month}
        onChange={handlers.handleMonthChange}
        onFocus={handlers.handleFocus}
        onBlur={handlers.handleBlur}
        onPaste={handlers.handlePaste}
        onKeyDown={handlers.handleKeyDown}
        disabled={disabled}
        maxLength={2}
        autoComplete={autoComplete === "bday" ? "bday-month" : autoComplete}
        style={{ width: "40px" }}
        aria-label="Mes"
      />
    );

    // Separador y input de año
    inputs.push(
      <DateSeparator key="sep2">/</DateSeparator>,
      <DateInput
        key="year"
        id={`${id}-year`}
        name={`${name}Year`}
        ref={yearRef}
        type="text"
        placeholder="aaaa"
        value={dateState.year}
        onChange={handlers.handleYearChange}
        onFocus={handlers.handleFocus}
        onPaste={handlers.handlePaste}
        onKeyDown={handlers.handleKeyDown}
        disabled={disabled}
        maxLength={4}
        autoComplete={autoComplete === "bday" ? "bday-year" : autoComplete}
        style={{ width: "60px" }}
        aria-label="Año"
      />
    );

    return inputs;
  };

  return (
    <InputContainer className={className}>
      <Label
        id={`${id}-label`}
        htmlFor={dateGranularity === "day" ? `${id}-day` : `${id}-month`}
      >
        {label}
        {required && <span aria-label="requerido"> *</span>}
      </Label>
      <InputWrapper>
        <DateInputContainer
          $hasHelpIcon={!!helpText}
          role="group"
          className="date-input-container"
          aria-labelledby={`${id}-label`}
        >
          {renderInputs()}
        </DateInputContainer>
        {helpText && (
          <HelpOverlay>
            <HelpArea aria-label="Ayuda" role="img">
              <Tooltip title={helpText} position={helpTooltipPosition}>
                <Icons name="iconInfo" width={24} height={24} />
              </Tooltip>
            </HelpArea>
          </HelpOverlay>
        )}
      </InputWrapper>
    </InputContainer>
  );
};

export default DateField;
